require('es6-shim'); //tslint:disable-line
import webBiLogger from '@wix/web-bi-logger';
import { devGeneralTwilioEvent, publicCloseAWidgetClick, publicNoSearchResults, publicPageView, publicPageViewArticle, publicPageViewCategory, publicSearch, publicSelectCategoryOrSubcategory, publicSelectSearchResult, publicSubmitTicketAttempt, publicSubmitTicketCancel, publicSubmitTicketSuccess, publicSubmitTicketValidationError, publicSuggestedArticlesClick, publicVoteForArticle, publicVoteForFeatureRequest, publicVoteForKnownIssue, publicWidgetOpenAWidget, publicWidgetSuggestedArticlesReport, publicWidgetSuggestedArticlesResolved, publicWidgetToggleModule } from '@wix/bi-logger-wix-answers/v2';
import { EventType } from '../event-type';
import {ArticleType} from '@wix/answers-api';
import { getAnonId, ensureAnonId } from '@wix/answers-bi';

const eventToPageTypeMap: {[key: number]: string} = {
	[EventType.CONTACT_VIEW]: 'widget-contact',
	[EventType.ARTICLE_NOT_FOUND]: 'widget-404',
	[EventType.KB_HOME_VIEW]: 'widget-home',
	[EventType.CHAT_VIEW]: 'widget-chat',
};

export const createBiEventHandler = (widgetId: string, locale: string, tenantId: string) => {
	try {
		ensureAnonId();
	} catch (e) {
		// tslint:disable-next-line:no-console
		console.error('error creating wix client id', e);
	}

	const logger = webBiLogger.factory().logger();

	const referralUrl = document.referrer;
	const capturedReferral = /referral=([^&]+)/.exec(referralUrl);
	const referralName = capturedReferral && capturedReferral[1] ? capturedReferral[1] : undefined;
	const browserTabId = Date.now().toString(36);

	const base = {
		locale,
		referral_name: referralName,
		referral_url: referralUrl,
		client_id: getAnonId() || undefined,
		tenant_id: tenantId
	};

	const basePublic = {...base, source_type: 'widget', source_id: widgetId};

	const baseWidget = {...base, widget_id: widgetId};

	const handleArticleVoteLogging = (type: ArticleType, data: any): Promise<void> => {
		switch (type) {
			case ArticleType.ARTICLE:
				return logger.report(publicVoteForArticle({
					...basePublic,
					thumbs_up: data.vote,
					article_id: data.article.id,
					category_id: data.article.categoryId
				}));
			case ArticleType.FEATURE_REQUEST:
				return logger.report(publicVoteForFeatureRequest({
					...basePublic,
					thumbs_up: data.vote,
					article_id: data.article.id,
					category_id: data.article.categoryId
				}));
			case ArticleType.KNOWN_ISSUE:
				return logger.report(publicVoteForKnownIssue({
					...basePublic,
					thumbs_up: data.vote,
					article_id: data.article.id,
					category_id: data.article.categoryId
				}));
			case ArticleType.VIDEO:
				return Promise.resolve();
		}
	};

	// tslint:disable-next-line:cyclomatic-complexity
	return (event: EventType, data: any): Promise<void> => {
		switch (event) {
			case EventType.OPEN:
				return logger.report(publicWidgetOpenAWidget({...baseWidget, open_method: data.openMethod}));
			case EventType.CLOSE:
				return logger.report(publicCloseAWidgetClick({...baseWidget, close_method: data.openMethod, module_name: data.moduleName}));
			case EventType.KB_HOME_VIEW:
			case EventType.CONTACT_VIEW:
			case EventType.ARTICLE_NOT_FOUND:
			case EventType.CHAT_VIEW:
				return logger.report(publicPageView({...basePublic, page_type: eventToPageTypeMap[event]}));
			case EventType.ARTICLE_VIEW: {
				const {articleId, articleTitle} = data;
				logger.report(publicPageViewArticle({...basePublic, article_id: articleId, article_name: articleTitle}));
				return logger.report(publicPageView({...basePublic, page_type: 'widget-article'}));
			}
			case EventType.CATEGORY_VIEW: {
				const {categoryId, categoryTitle} = data;
				logger.report(publicPageViewCategory({...basePublic, category_id: categoryId, category_name: categoryTitle}));
				return logger.report(publicPageView({...basePublic, page_type: 'widget-category'}));
			}
			case EventType.TOGGLE_MODULE:
				return logger.report(publicWidgetToggleModule({...baseWidget, switch_to: data.nextModule}));
			case EventType.SEARCH:
				return logger.report(publicSearch({...basePublic, search_term: data.term, result_article_id_1: data.firstResultId}));
			case EventType.SEARCH_NO_RESULT:
				return logger.report(publicNoSearchResults({...basePublic, search_term: data.term}));
			case EventType.SEARCH_RESULT_CLICK:
				return logger.report(publicSelectSearchResult({
					...basePublic,
					search_term: data.term,
					article_id: data.articleId,
					answer: data.articleTitle,
					answer_index: data.position + 1,
				}));
			case EventType.ARTICLE_VOTE:
			case EventType.ARTICLE_UNVOTE:
				return handleArticleVoteLogging(data.article.type, data);
			case EventType.ARTICLE_HELPFUL:
				return logger.report(publicWidgetSuggestedArticlesResolved({
					...baseWidget,
					article_id: data.article.id,
					article_name: data.article.title
				}));
			case EventType.ARTICLE_UNHELPFUL:
				return logger.report(publicWidgetSuggestedArticlesReport({
					...baseWidget,
					article_id: data.article.id,
					article_name: data.article.title
				}));
			case EventType.SELECT_CATEGORY:
				return logger.report(publicSelectCategoryOrSubcategory({
					...basePublic,
					category_id: data.category.id,
					category_name: data.category.name
				}));
			case EventType.SUBMIT_TICKET_ATTEMPT:
				return logger.report(publicSubmitTicketAttempt({
					...basePublic,
					subject: data.title,
					description: data.description,
					is_after_suggestion: data.isAfterSuggestion,
					is_attached: !!data.attachments.length,
					form_source: 'widget'
				}));
			case EventType.SUBMIT_TICKET_SUCCESS:
				return logger.report(publicSubmitTicketSuccess({
					...basePublic,
					subject: data.title,
					description: data.description,
					is_after_suggestion: data.isAfterSuggestion,
					is_attached: !!data.attachments.length,
					form_source: 'widget',
					ticket_id: data.ticketId
				}));
			case EventType.SUBMIT_TICKET_VALIDATION_ERROR:
				return logger.report(publicSubmitTicketValidationError({
					...basePublic,
					subject: data.title,
					description: data.description,
					is_after_suggestion: data.isAfterSuggestion,
					is_attached: !!data.attachments.length,
					form_source: 'widget',
					error_in_subject: data.invalidFields.title,
					error_in_description: data.invalidFields.description,
					error_in_name: data.invalidFields.name,
					error_in_email: data.invalidFields.email,
					error_in_costume_field: data.invalidFields.customFields
				}));
			case EventType.SUBMIT_TICKET_CANCEL:
				return logger.report(publicSubmitTicketCancel({
					...basePublic,
					subject: data.title,
					description: data.description,
					is_attached: !!data.attachments.length,
					form_source: 'widget'
				}));
			case EventType.SUBMIT_TICKET_SELECT_SUGGESTED_ARTICLE:
				return logger.report(publicSuggestedArticlesClick({
					...basePublic,
					subject: data.title,
					description: data.description,
					form_source: 'widget',
					selected_article_id: data.article.id,
					selected_article_name: data.article.title
				}));
			case EventType.CHAT_STARTED:
			case EventType.CHAT_STARTED_SUCCESS:
			case EventType.CHAT_STARTED_FAIL:
			case EventType.UPDATE_EMAIL:
			case EventType.UPDATE_EMAIL_SUCCESS:
			case EventType.UPDATE_EMAIL_FAIL:
			case EventType.SUBSCRIBE_USER_TIMELINE:
			case EventType.SUBSCRIBE_USER_TIMELINE_SUCCESS:
			case EventType.SUBSCRIBE_USER_TIMELINE_FAIL:
			case EventType.SEND_CHAT_MESSAGE:
			case EventType.SEND_CHAT_MESSAGE_SUCCESS:
			case EventType.SEND_CHAT_MESSAGE_FAIL:
			case EventType.RECEIVE_CHAT_MESSAGE:
			case EventType.PUBSUB_EVENT:
			case EventType.CHAT_UNLOAD:
			case EventType.CHAT_HIDDEN:
			case EventType.CHAT_VISIBLE:
			case EventType.CHAT_TIMELINE_UPDATED:
			case EventType.CHAT_ONLINE:
			case EventType.SUBMIT_EMAIL_FORM:
				return logger.report(devGeneralTwilioEvent({
					client_id: base.client_id,
					tenant_id: base.tenant_id,
					tab_id: browserTabId,
					type: event === EventType.PUBSUB_EVENT ? data.type : EventType[event].replace(/_/g, ' ').toLowerCase(),
					data: JSON.stringify(data),
					user_id: data.userId,
					item_id: data.ticketId
				}));
			case EventType.FIRST_OPEN:
			case EventType.LOAD:
			case EventType.SEARCH_IGNORED:
			case EventType.SUBMIT_TICKET_FAIL:
				// These events are not currently logged to source 77
				return Promise.resolve();
		}
	};
};
